import { useState } from "react";
import { t } from "i18next";

import { GROUP_KIND } from "../../helpers/hangingProtocol/protocolEnums";
import * as hpv2 from "../../service/HPV2Service";

/**
 * Shared bits of the two hanging protocol V2 wizards.
 *
 * The step status icons are the same three the v1 wizard uses, so both screens
 * read alike: orange while a step has not been through validation, green once it
 * has, red when submitting found something wrong.
 */
export const TAB_STATUS = {
  PENDING: { value: "Info", color: "#FF9900" },
  DONE: { value: "Completed", color: "green" },
  ERROR: { value: "Info", color: "red" },
};

/** Server kind enum -> the document collection a group belongs to. */
const KIND_TO_GROUP_KIND = {
  [hpv2.HP_KIND.STANDARD]: GROUP_KIND.STANDARD,
  [hpv2.HP_KIND.DRAG]: GROUP_KIND.DRAG,
  [hpv2.HP_KIND.COMPARISON]: GROUP_KIND.COMPARISON,
};

export const toGroupKind = (kind) => KIND_TO_GROUP_KIND[kind] ?? GROUP_KIND.STANDARD;

/** Every kind a group can arrive from the server as, labelled. */
const KIND_LABEL_KEY = {
  [hpv2.HP_KIND.STANDARD]: "hanging-protocol.kind-standard",
  [hpv2.HP_KIND.DRAG]: "hanging-protocol.kind-drag",
  [hpv2.HP_KIND.COMPARISON]: "hanging-protocol.kind-comparison",
};

export const kindLabel = (kind) => t(KIND_LABEL_KEY[kind] ?? KIND_LABEL_KEY[hpv2.HP_KIND.STANDARD]);

/**
 * The kinds a group may be created as.
 *
 * Drag & drop and comparison groups are not offered yet, so standard is the only
 * entry: the wizard hides the Type field while there is nothing to choose and
 * brings it back — dropdown, note and all — as soon as a second kind is listed
 * here. Groups already saved under another kind still load, edit and save under
 * the kind they have; only creating a new one is restricted.
 */
export const CREATABLE_KINDS = [hpv2.HP_KIND.STANDARD];

export const kindOptions = () => CREATABLE_KINDS.map((kind) => ({ key: kind, text: kindLabel(kind) }));

/**
 * Step icons for the two V2 wizards.
 *
 * A step is *settled* once the user has left it — by clicking another step, or
 * with Next/Previous. Only then is there anything to report about it, so until
 * then a new protocol's steps stay orange. A settled step is green, or red if
 * something on it is still missing. Editing starts settled: every step already
 * holds saved values.
 *
 * The wizard container reports the step being left as its element id
 * (`hpv2protocol_2`) on a click and as a bare number on Next/Previous, so both
 * are resolved against the ordered step ids the screen passes in.
 */
export const useStepStatus = ({ adding, readOnly, submitted }) => {
  const [settled, setSettled] = useState(() => new Set());

  const settle = (id) => {
    if (!id) return;
    setSettled((current) => (current.has(id) ? current : new Set(current).add(id)));
  };

  const status = (id, invalid = false) => {
    if (readOnly) return TAB_STATUS.DONE;
    const seen = !adding || settled.has(id);
    if (invalid && (submitted || settled.has(id))) return TAB_STATUS.ERROR;
    return seen ? TAB_STATUS.DONE : TAB_STATUS.PENDING;
  };

  /** Tab container callbacks that settle whichever step is being left. */
  const stepLeftHandlers = (orderedIds) => {
    const leaving = (value) => {
      const order = Number(String(value ?? "").split("_").pop());
      return Number.isFinite(order) ? orderedIds[order - 1] : undefined;
    };
    const onLeave = (value) => settle(leaving(value));
    return {
      tabValidation: (_selected, previous) => onLeave(previous),
      onNextClick: onLeave,
      onPreviousClick: onLeave,
    };
  };

  return { status, settle, stepLeftHandlers };
};

/**
 * Clamps a `?tab=` parameter to the steps a screen actually has. The tab
 * container looks its initial step up by element id and a missing one would
 * leave the wizard with nothing selected.
 */
export const clampTab = (value, stepCount) => {
  const asNumber = Number(value);
  if (!Number.isFinite(asNumber) || asNumber < 1) return "1";
  return String(Math.min(Math.trunc(asNumber), Math.max(1, stepCount)));
};
